import React, { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Every page is its own code-split chunk, so visitors only download the page they open.
// `page()` wraps React.lazy with a `preload()` that index.js awaits for the landing
// route before the first render, so the page renders in one pass (no loading flash).
const page = (importer) => {
  let Loaded = null;
  const load = () => importer().then((module) => {
    Loaded = module.default;
    return module;
  });
  const Lazy = lazy(load);
  const Page = (props) => (Loaded ? <Loaded {...props} /> : <Lazy {...props} />);
  Page.preload = load;
  return Page;
};

export const ROUTES = [
  { path: '/', page: page(() => import('../pages/Home1')) },
  { path: '/industries', page: page(() => import('../pages/Industries')) },
  { path: '/industries/accounting-bookkeeping', page: page(() => import('../pages/industries/AccountingBookkeeping')) },
  { path: '/industries/coaching-training', page: page(() => import('../pages/industries/CoachingTraining')) },
  { path: '/industries/hospitality', page: page(() => import('../pages/industries/Hospitality')) },
  { path: '/industries/consulting', page: page(() => import('../pages/industries/Consulting')) },
  { path: '/industries/digital-marketing-agencies', page: page(() => import('../pages/industries/DigitalMarketingAgencies')) },
  { path: '/industries/ecommerce', page: page(() => import('../pages/industries/Ecommerce')) },
  { path: '/industries/education-edtech', page: page(() => import('../pages/industries/EducationEdtech')) },
  { path: '/industries/financial-advisory', page: page(() => import('../pages/industries/FinancialAdvisory')) },
  { path: '/industries/healthcare', page: page(() => import('../pages/industries/HealthcareAdmin')) },
  { path: '/industries/real-estate', page: page(() => import('../pages/industries/RealEstate')) },
  { path: '/industries/property-management', page: page(() => import('../pages/industries/PropertyManagement')) },
  { path: '/industries/vacation-rentals', page: page(() => import('../pages/industries/VacationRentals')) },
  { path: '/industries/legal-services', page: page(() => import('../pages/industries/LegalServices')) },
  { path: '/industries/nonprofit-organizations', page: page(() => import('../pages/industries/NonProfits')) },
  { path: '/industries/podcasting-content-creation', page: page(() => import('../pages/industries/PodcastingContentCreators')) },
  { path: '/industries/recruitment-staffing', page: page(() => import('../pages/industries/RecruitmentStaffing')) },
  { path: '/industries/small-business', page: page(() => import('../pages/industries/SmallBusiness')) },
  { path: '/industries/startups-entrepreneurs', page: page(() => import('../pages/industries/StartupsEntrepreneurs')) },
  { path: '/about-us', page: page(() => import('../pages/About')) },
  { path: '/contact-us', page: page(() => import('../pages/Contact')) },
  { path: '/how-we-work', page: page(() => import('../pages/HowWeWork')) },
  { path: '/how-to-delegate', page: page(() => import('../pages/HowToDelegate')) },
  { path: '/why-getfriday', page: page(() => import('../pages/WhyGetFriday')) },
  { path: '/faqs', page: page(() => import('../pages/FAQs')) },
  { path: '/refer-a-friend', page: page(() => import('../pages/ReferAFriend')) },
  { path: '/gift-of-time', page: page(() => import('../pages/GiftOfTime')) },
  { path: '/business/quote', page: page(() => import('../pages/business/Quote')) },
  { path: '/testimonials', page: page(() => import('../pages/Testimonials')) },
  { path: '/press-and-media', page: page(() => import('../pages/PressAndMedia')) },
  { path: '/videos-and-podcasts', page: page(() => import('../pages/VideosAndPodcasts')) },
  { path: '/personal', page: page(() => import('../pages/personal/VirtualPersonalAssistant')) },
  { path: '/personal/virtual-personal-assistant', page: page(() => import('../pages/personal/VirtualPersonalAssistant')) },
  { path: '/personal/family-and-personal', page: page(() => import('../pages/personal/FamilyAndPersonal')) },
  { path: '/personal/travel-management', page: page(() => import('../pages/personal/TravelManagement')) },
  { path: '/personal/online-research', page: page(() => import('../pages/personal/OnlineResearch')) },
  { path: '/personal/remote-executive-secretary', page: page(() => import('../pages/personal/RemotePersonalAssistant')) },
  { path: '/personal/sundry-tasks', page: page(() => import('../pages/personal/SundryTasks')) },
  { path: '/personal/ai-guidance', page: page(() => import('../pages/personal/AiForPersonalLife')) },
  { path: '/personal/ai-for-life', redirect: '/personal/ai-guidance' },
  { path: '/personal/ai-for-personal-life', redirect: '/personal/ai-guidance' },
  { path: '/personal/pricing-plans', page: page(() => import('../pages/personal/PricingPlans')) },
  { path: '/business', page: page(() => import('../pages/business/Services')) },
  { path: '/business/services', page: page(() => import('../pages/business/Services')) },
  { path: '/business/business-process-support', page: page(() => import('../pages/business/BusinessProcessSupport')) },
  { path: '/business/website-services', page: page(() => import('../pages/business/WebsiteServices')) },
  { path: '/business/digital-marketing', page: page(() => import('../pages/business/DigitalMarketing')) },
  { path: '/business/bookkeeping-service', page: page(() => import('../pages/business/BookkeepingService')) },
  { path: '/business/bookkeeping-service/quickbooks-bookkeeping', page: page(() => import('../pages/business/bookkeeping/QuickBooksBookkeeping')) },
  { path: '/business/research-analytics', page: page(() => import('../pages/business/ResearchAnalytics')) },
  { path: '/business/odoo-implementation', page: page(() => import('../pages/business/OdooImplementation')) },
  { path: '/business/ai-automation', page: page(() => import('../pages/business/AIAutomation')) },
  { path: '/business/remote-executive-secretary', page: page(() => import('../pages/business/RemoteExecutiveSecretary')) },
  { path: '/business/vacation-rental', page: page(() => import('../pages/business/VacationRental')) },
  { path: '/4-hour-workweek-ymii', page: page(() => import('../pages/FourHourWorkweekYmii')) },
  { path: '/privacy-policy', page: page(() => import('../pages/PrivacyPolicy')) },
  { path: '/terms-and-conditions', page: page(() => import('../pages/TermsAndConditions')) },
  { path: '/refund-policy', page: page(() => import('../pages/RefundPolicy')) },
  { path: '/disclaimer', page: page(() => import('../pages/Disclaimer')) },
  { path: '/copyright', page: page(() => import('../pages/Copyright')) },
  { path: '/sitemap', page: page(() => import('../pages/Sitemap')) },
  { path: '/page-not-found', page: page(() => import('../pages/PageNotFound')) },
  { path: '/Thankyou', page: page(() => import('../pages/ThankYou')) },
  { path: '/digital-marketing-lp2026', page: page(() => import('../pages/campaign/DigitalMarketingLP2026')) },
  { path: '/thankyou-digital-marketing-lp2026', page: page(() => import('../pages/campaign/ThankYouDigitalMarketingLP2026')) },
  { path: '/american-independence-campaign-2026', page: page(() => import('../pages/campaign/AmericanIndependenceCampaign2026')) },
  { path: '/thankyou-250-independence-campaign', page: page(() => import('../pages/campaign/ThankYouAmericanIndependence2026')) },
];

const normalize = (path) => (path.replace(/\/+$/, '') || '/').toLowerCase();

// Loads the code for the page at `pathname` (no-op for unknown paths / redirects).
export const preloadRoute = (pathname) => {
  const route = ROUTES.find((r) => r.page && normalize(r.path) === normalize(pathname));
  return route ? route.page.preload() : Promise.resolve();
};

const AppRoutes = () => (
  <Routes>
    {ROUTES.map(({ path, page: Page, redirect }) => (
      <Route
        key={path + (redirect ? '>' : '')}
        path={path}
        element={redirect ? <Navigate to={redirect} replace /> : <Page />}
      />
    ))}
    {/* Any URL not in ROUTES (404) goes to the Page Not Found page. */}
    <Route path="*" element={<Navigate to="/page-not-found" replace />} />
  </Routes>
);

export default AppRoutes;
